import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Trash2, X } from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const AiAssistant = ({ open, onClose }) => {
  const { t } = useI18n();
  const SUGGESTIONS = [t("ai.s1"), t("ai.s2"), t("ai.s3")];
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) api.get("/ai/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem("op_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": localStorage.getItem("op_lang") || "it",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ message: question, session_id: "default" }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          const payload = JSON.parse(line);
          if (payload.delta)
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + payload.delta,
              };
              return copy;
            });
          if (payload.error)
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: `Errore: ${payload.error}` };
              return copy;
            });
        }
      }
    } catch (e) {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: t("ai.connError") }]);
    } finally {
      setStreaming(false);
    }
  };

  const clear = async () => {
    await api.delete("/ai/messages");
    setMessages([]);
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} />
      <aside
        data-testid="ai-panel"
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-indigo-500/30 bg-slate-950 text-slate-100"
      >
        <div className="flex items-center justify-between border-b border-indigo-500/25 px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-400" />
            <div>
              <p className="text-sm font-extrabold tracking-tight">Operativo AI</p>
              <p className="text-xs text-slate-400">Conosce clienti, documenti e attività</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="ai-clear"
              onClick={clear}
              className="rounded-md p-2 text-slate-400 transition-colors duration-200 hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              data-testid="ai-close"
              onClick={onClose}
              className="rounded-md p-2 text-slate-400 transition-colors duration-200 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="scroll-thin flex-1 space-y-4 overflow-y-auto px-5 py-6" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-400">{t("ai.intro")}</p>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid="ai-suggestion"
                  onClick={() => send(s)}
                  className="block w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2.5 text-left text-sm text-slate-300 transition-colors duration-200 hover:border-indigo-500/50 hover:text-white"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : ""}>
              <div
                className={`inline-block max-w-[90%] whitespace-pre-wrap rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-800 bg-slate-900 text-slate-200"
                }`}
              >
                {m.content || (
                  <span className="flex gap-1">
                    <span className="typing-dot h-1.5 w-1.5 rounded-full bg-indigo-400" />
                    <span className="typing-dot h-1.5 w-1.5 rounded-full bg-indigo-400" style={{ animationDelay: "0.2s" }} />
                    <span className="typing-dot h-1.5 w-1.5 rounded-full bg-indigo-400" style={{ animationDelay: "0.4s" }} />
                  </span>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-indigo-500/25 p-4">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder={t("ai.placeholder")}
              className="scroll-thin flex-1 resize-none rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-indigo-500"
            />
            <Button
              data-testid="ai-send"
              onClick={() => send()}
              disabled={streaming}
              className="h-10 bg-indigo-600 text-white hover:bg-indigo-500"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AiAssistant;
